import { reportOnce } from '@protector/api-client';
import type { CurrentSeasonAreas, Phenomenon } from '@protector/entities/phenomenon/models';
import { LoadMethodologiesSuccess } from '@protector/entities/region/__legacy__/redux.actions';
import type { Dictionary, UUID } from '@protector/utils/types';
import { ofType, type ActionsObservable } from 'redux-observable';
import { concat, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import {
  LoadMethodology,
  LoadMethodologyByAreaFailure,
  LoadMethodologyByAreaSuccess,
  LoadMethodologyFailure,
  LoadMethodologySuccess,
  LoadPhenomenonByMethodology,
  LoadPhenomenonByMethodologyFailure,
  LoadPhenomenonByMethodologySuccess,
  PhenomenonActionsTypes,
  type LoadMethodologies,
  type LoadMethodologyByArea
} from './phenomenon.actions';
import { getMethodologies, getMethodology, getPhenomenaByMethodology } from './phenomenon.service';

const findPhenomenonMethodologyIds = (methodologyPhenomena: Dictionary<Phenomenon[]>, phenomenonId: UUID): UUID[] =>
  Object.keys(methodologyPhenomena).filter(m => methodologyPhenomena[m].some(p => p.id === phenomenonId));

export const parsePhenomena = (methodologyPhenomena: Dictionary<Phenomenon[]>): Phenomenon[] => {
  return Object.keys(methodologyPhenomena).flatMap(key =>
    methodologyPhenomena[key].map(p => ({
      ...p,
      methodologyIds: findPhenomenonMethodologyIds(methodologyPhenomena, p.id)
    }))
  );
};

interface ParsedArea {
  seasonId: UUID;
  methodologyId: UUID;
}

const parseAreas = (seasonAreas: CurrentSeasonAreas[]): Dictionary<ParsedArea> => {
  const parsedAreas: Dictionary<ParsedArea> = {};
  seasonAreas.forEach(seasonArea => {
    parsedAreas[seasonArea.areaId] = {
      seasonId: seasonArea.seasonId,
      // methodologyId is a legacy dependency on the season-area payload (see the
      // @deprecated notes on v1/season-areas in the services layer)
      methodologyId: seasonArea.methodologyId
    };
  });

  return parsedAreas;
};

export const handleLoadMethodologies = (action$: ActionsObservable<ReturnType<typeof LoadMethodologies>>) =>
  action$.pipe(
    ofType(PhenomenonActionsTypes.LOAD_METHODOLOGIES),
    map(action => action.payload),
    concatMap(propertyId =>
      getMethodologies(propertyId).pipe(
        concatMap(seasonAreas => {
          const uniqueMethodologies = seasonAreas.reduce<UUID[]>((acc, seasonArea) => {
            // methodologyId is a legacy dependency on the season-area payload (see the
            // @deprecated notes on v1/season-areas in the services layer)
            if (acc.includes(seasonArea.methodologyId) || !seasonArea.methodologyId) return acc;
            acc.push(seasonArea.methodologyId);
            return acc;
          }, []);
          const parsedAreas = parseAreas(seasonAreas);
          return concat([LoadMethodologiesSuccess(parsedAreas), LoadPhenomenonByMethodology(uniqueMethodologies)]);
        })
      )
    )
  );

export const handleLoadPhenomenaByMethodology = (action$: ActionsObservable<ReturnType<typeof LoadPhenomenonByMethodology>>) =>
  action$.pipe(
    ofType(PhenomenonActionsTypes.LOAD_PHENOMENON_BY_METHODOLOGY),
    map(action => action.payload),
    concatMap(methodologyIds =>
      getPhenomenaByMethodology(methodologyIds).pipe(
        map(response => {
          if (!response) {
            reportOnce(
              'phenomenon.epics:handleLoadPhenomenaByMethodology:missingResponse',
              () => new Error('phenomenon.epics: getPhenomenaByMethodology response missing -- falling back to []'),
              { errorType: 'StateFallback', domain: 'Phenomenon' }
            );
          }
          const parsedMethodologies = (response ?? []).reduce((acc, val) => {
            return {
              ...acc,
              [Object.keys(val)[0]]: Object.values(val)[0]
            };
          }, {});
          const parsedPhenomena = parsePhenomena(parsedMethodologies);
          return LoadPhenomenonByMethodologySuccess(parsedPhenomena);
        })
      )
    ),
    catchError((err: string) => of(LoadPhenomenonByMethodologyFailure(err)))
  );

const toMethodologyAreaAction = (id: UUID, response: CurrentSeasonAreas[] | null) => {
  if (!response) {
    reportOnce(
      'phenomenon.epics:handleLoadMethodologyArea:missingResponse',
      () => new Error('phenomenon.epics: getMethodologies response missing -- falling back to []'),
      { errorType: 'StateFallback', domain: 'Phenomenon' }
    );
  }
  const methodologyAreas = response ?? [];
  const methodologyArea = methodologyAreas.find(item => item.areaId === id);
  if (methodologyArea?.methodologyId) {
    return LoadMethodology(methodologyArea.methodologyId);
  }
  return LoadMethodologyByAreaSuccess(methodologyAreas);
};

export const handleLoadMethodologyArea = (action$: ActionsObservable<ReturnType<typeof LoadMethodologyByArea>>) =>
  action$.pipe(
    ofType(PhenomenonActionsTypes.LOAD_METHODOLOGY_BY_AREA),
    map(action => action.payload),
    concatMap(({ propertyId, id, endsAt }) =>
      getMethodologies(propertyId, endsAt).pipe(
        map(response => toMethodologyAreaAction(id, response)),
        catchError((error: string) => of(LoadMethodologyByAreaFailure(error)))
      )
    )
  );

export const handleLoadMethodology = (action$: ActionsObservable<ReturnType<typeof LoadMethodology>>) =>
  action$.pipe(
    ofType(PhenomenonActionsTypes.LOAD_METHODOLOGY),
    map(action => action.payload),
    concatMap(payload => {
      return getMethodology(payload).pipe(
        map(response => {
          return LoadMethodologySuccess(response);
        }),
        catchError((error: string) => of(LoadMethodologyFailure(error)))
      );
    })
  );
